import { extendTheme, type ThemeConfig } from "@chakra-ui/react"

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
}

const theme = extendTheme({
  config,
  colors: {
    /**
     * Core brand palette based on the Lead Supplai email:
     * - Primary blue: #3b5ea8
     * - Deep blue:    #1f3b70
     */
    primary: {
      50:  "#e9f0ff",
      100: "#c7d6f3",
      200: "#a4bce7",
      300: "#7a9cdd",
      400: "#567fcd",
      500: "#3b5ea8", // Main brand blue (subtitle + icons)
      600: "#334f94",
      700: "#1f3b70", // Deep heading/button blue
      800: "#182f5a",
      900: "#122241",
    },

    silver: {
      50:  "#F2F2F2",
      100: "#E6E6E6",
      200: "#D9D9D9",
      300: "#BFBFBF",
      400: "#A6A6A6",
      500: "#999999",
      600: "#7F7F7F",
      700: "#666666",
      800: "#4D4D4D",
      900: "#333333",
    },

    // Accent tokens (you can still use these for special effects if needed)
    blueGlow:   "#3b5ea8",
    orangeGlow: "#FF801A",

    // Convenience alias so `brand.*` == primary.*
    brand: {
      50:  "#e9f0ff",
      100: "#c7d6f3",
      200: "#a4bce7",
      300: "#7a9cdd",
      400: "#567fcd",
      500: "#3b5ea8",
      600: "#334f94",
      700: "#1f3b70",
      800: "#182f5a",
      900: "#122241",
    },
  },

  fonts: {
    heading:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    body:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  },

  components: {
    Button: {
      defaultProps: {
        colorScheme: "primary",
        variant: "solid",
      },
      variants: {
        solid: {
          bg: "primary.600",
          color: "white",
          _hover: { bg: "primary.700" },
          _active: { bg: "primary.800" },
        },
        outline: {
          color: "primary.600",
          borderColor: "primary.600",
          _hover: {
            bg: "primary.50",
            borderColor: "primary.700",
            color: "primary.700",
          },
        },
        ghost: {
          color: "primary.600",
          _hover: { bg: "primary.50" },
        },
        // Glow accents if you want special CTAs
        blueGlow: {
          bg: "blueGlow",
          color: "white",
          _hover: { filter: "brightness(0.95)" },
        },
        orangeGlow: {
          bg: "orangeGlow",
          color: "black",
          _hover: { filter: "brightness(0.95)" },
        },
        // Brand gradient aligned with the email CTA button
        brandGradient: {
          bgGradient: "linear(135deg, #3b5ea8, #1f3b70)",
          color: "white",
          border: "none",
          px: "30px",
          py: "12px",
          fontSize: "16px",
          transition: "all 0.3s ease",
          overflow: "visible",
          boxShadow: "0 6px 18px rgba(31, 59, 112, 0.5)",

          _hover: {
            bgGradient: "linear(135deg, #3b5ea8, #1f3b70)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 18px rgba(31, 59, 112, 0.5)",
            filter: "brightness(0.95)",
          },
          _active: {
            bgGradient: "linear(135deg, #3b5ea8, #1f3b70)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 18px rgba(31, 59, 112, 0.5)",
            filter: "brightness(0.9)",
          },
          _focusVisible: {
            bgGradient: "linear(135deg, #3b5ea8, #1f3b70)",
            outline: "0",
            boxShadow:
              "0 0 0 2px rgba(255,255,255,0.7), 0 6px 18px rgba(31, 59, 112, 0.5)",
          },
          _disabled: {
            bgGradient: "linear(135deg, #3b5ea8, #1f3b70)",
            boxShadow: "0 6px 18px rgba(31, 59, 112, 0.3)",
            opacity: 0.7,
            cursor: "not-allowed",
          },
          _loading: {
            bgGradient: "linear(135deg, #3b5ea8, #1f3b70)",
            color: "white",
            boxShadow: "0 6px 18px rgba(31, 59, 112, 0.5)",
            _hover: {
              bgGradient: "linear(135deg, #3b5ea8, #1f3b70)",
              transform: "none",
              filter: "none",
              boxShadow: "0 6px 18px rgba(31, 59, 112, 0.5)",
            },
            _active: {
              bgGradient: "linear(135deg, #3b5ea8, #1f3b70)",
              transform: "none",
              filter: "none",
            },
          },
        },
      },
    },

    Card: {
      baseStyle: {
        container: {
          borderRadius: "md",
          boxShadow: "sm",
          bg: "white",
          color: "black",
        },
        header: { pb: 0 },
      },
    },

    Input: {
      variants: {
        outline: {
          field: {
            bg: "white",
            color: "black",
            borderColor: "primary.100",
            _placeholder: { color: "silver.500" },
            _focus: {
              borderColor: "primary.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            },
          },
        },
      },
    },

    Select: {
      variants: {
        outline: {
          field: {
            bg: "white",
            color: "black",
            borderColor: "primary.100",
            _placeholder: { color: "silver.500" },
            _focus: {
              borderColor: "primary.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            },
          },
        },
      },
    },

    Textarea: {
      variants: {
        outline: {
          bg: "white",
          color: "black",
          borderColor: "primary.100",
          _placeholder: { color: "silver.500" },
          _focus: {
            borderColor: "primary.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
          },
        },
      },
    },

    Link: {
      baseStyle: {
        color: "primary.600",
        _hover: { textDecoration: "none", color: "primary.700" },
      },
    },

    Table: {
      variants: {
        simple: {
          table: { bg: "white", color: "black" },
          th: {
            borderBottom: "1px",
            borderColor: "silver.200",
            fontWeight: "semibold",
            p: 3,
            textTransform: "none",
            letterSpacing: "normal",
            color: "black",
          },
          td: {
            borderBottom: "1px",
            borderColor: "silver.100",
            p: 3,
            color: "black",
          },
        },
      },
    },
  },

  styles: {
    global: {
      body: {
        bg: "white",
        color: "black",
      },
    },
  },
})

export default theme
